/**
 * Execution block: controls video playback on a SceneObject.
 *
 * Config: target (path string)
 * Data inputs: play (bool), volume (float 0-1), seek (float seconds)
 * All inputs are optional — omitted/unconnected inputs leave that aspect unchanged.
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeBoolean, RichTypeNumber } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Video } from '../../video';
import { Logger } from '../../../utils/Logger';
import { resolveTarget } from './traceContext';

export class TracePlayVideoBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("play", RichTypeBoolean, undefined);
        this.registerDataInput("volume", RichTypeNumber, -1);
        this.registerDataInput("seek", RichTypeNumber, -1);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.out._activateSignal(context);
            return;
        }

        const videoBehavior = target.getBehavior(Video);
        if (!videoBehavior) {
            Logger.warn(`[TracePlayVideoBlock] No Video behavior on ${target.name}`);
            this.out._activateSignal(context);
            return;
        }

        const volume = this.getDataInput("volume")!.getValue(context) as number;
        if (volume >= 0) videoBehavior.setVolume(volume);

        const seek = this.getDataInput("seek")!.getValue(context) as number;
        if (seek >= 0) videoBehavior.seek(seek);

        const play = this.getDataInput("play")!.getValue(context) as boolean | undefined;
        if (play === true) videoBehavior.play();
        else if (play === false) videoBehavior.pause();

        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TracePlayVideoBlock";
    }
}
